// Elimination-only techniques used by LogicalSolver: naked-pair,
// pointing-pair (box → line), and box-line reduction (line → box). They
// shrink the candidate state instead of placing a digit; the solver applies
// the resulting eliminations and loops back to the easier techniques.

import { BOARD_SIZE, type NonEmptyDigit } from './types.js';
import {
  bit,
  boxOfIndex,
  cellsOfUnit,
  popcount,
  type CandidateState,
  type Unit,
  type UnitKind,
} from './Candidates.js';

export interface Elimination {
  readonly cellIndex: number;
  readonly digit: NonEmptyDigit;
}

export interface NakedPairStep {
  readonly technique: 'naked-pair';
  readonly unit: Unit;
  readonly cells: readonly [number, number];
  readonly digits: readonly [NonEmptyDigit, NonEmptyDigit];
  readonly eliminations: readonly Elimination[];
}

export interface BoxLineStep {
  readonly technique: 'box-line';
  readonly direction: 'box-to-line' | 'line-to-box';
  readonly box: number;
  readonly line: Unit;
  readonly digit: NonEmptyDigit;
  /** Cells in the intersection where the digit is confined. */
  readonly anchorCells: readonly number[];
  readonly eliminations: readonly Elimination[];
}

function digitsOfMask(mask: number): NonEmptyDigit[] {
  const out: NonEmptyDigit[] = [];
  let m = mask;
  while (m !== 0) {
    const lowBit = m & -m;
    m ^= lowBit;
    out.push((31 - Math.clz32(lowBit)) as NonEmptyDigit);
  }
  return out;
}

function findNakedPairInUnit(unit: Unit, state: CandidateState): NakedPairStep | null {
  const cells = cellsOfUnit(unit);
  for (let i = 0; i < cells.length; i++) {
    const ai = cells[i] as number;
    const am = state.cells[ai] ?? 0;
    if (popcount(am) !== 2) continue;
    for (let j = i + 1; j < cells.length; j++) {
      const bj = cells[j] as number;
      if ((state.cells[bj] ?? 0) !== am) continue;
      const eliminations: Elimination[] = [];
      for (const k of cells) {
        if (k === ai || k === bj) continue;
        const shared = (state.cells[k] ?? 0) & am;
        if (shared === 0) continue;
        for (const d of digitsOfMask(shared)) eliminations.push({ cellIndex: k, digit: d });
      }
      if (eliminations.length === 0) continue;
      const [d1, d2] = digitsOfMask(am) as [NonEmptyDigit, NonEmptyDigit];
      return { technique: 'naked-pair', unit, cells: [ai, bj], digits: [d1, d2], eliminations };
    }
  }
  return null;
}

export function findNakedPair(state: CandidateState): NakedPairStep | null {
  const kinds: UnitKind[] = ['row', 'col', 'box'];
  for (const kind of kinds) {
    for (let idx = 0; idx < BOARD_SIZE; idx++) {
      const step = findNakedPairInUnit({ kind, index: idx }, state);
      if (step !== null) return step;
    }
  }
  return null;
}

function makeBoxLineStep(
  direction: BoxLineStep['direction'],
  box: number,
  line: Unit,
  digit: NonEmptyDigit,
  anchorCells: readonly number[],
  eliminations: readonly Elimination[],
): BoxLineStep {
  return { technique: 'box-line', direction, box, line, digit, anchorCells, eliminations };
}

// Cells where `digit` is a candidate within `unit`.
function digitCellsInUnit(unit: Unit, digit: NonEmptyDigit, state: CandidateState): number[] {
  const b = bit(digit);
  const out: number[] = [];
  for (const i of cellsOfUnit(unit)) {
    if (((state.cells[i] ?? 0) & b) !== 0) out.push(i);
  }
  return out;
}

// If every cell shares a row, return ('row', that row). Same for col. Else null.
function sharedLine(cells: readonly number[]): { kind: 'row' | 'col'; index: number } | null {
  if (cells.length < 2) return null;
  const first = cells[0] as number;
  const row0 = Math.floor(first / BOARD_SIZE);
  const col0 = first % BOARD_SIZE;
  let sameRow = true;
  let sameCol = true;
  for (let i = 1; i < cells.length; i++) {
    const c = cells[i] as number;
    if (Math.floor(c / BOARD_SIZE) !== row0) sameRow = false;
    if (c % BOARD_SIZE !== col0) sameCol = false;
  }
  if (sameRow) return { kind: 'row', index: row0 };
  if (sameCol) return { kind: 'col', index: col0 };
  return null;
}

export function findPointingPair(state: CandidateState): BoxLineStep | null {
  for (let box = 0; box < BOARD_SIZE; box++) {
    for (let n = 1; n <= BOARD_SIZE; n++) {
      const d = n as NonEmptyDigit;
      const inBox = digitCellsInUnit({ kind: 'box', index: box }, d, state);
      const match = sharedLine(inBox);
      if (match === null) continue;
      const line: Unit = { kind: match.kind, index: match.index };
      const dBit = bit(d);
      const eliminations: Elimination[] = [];
      for (const i of cellsOfUnit(line)) {
        if (boxOfIndex(i) === box) continue;
        if (((state.cells[i] ?? 0) & dBit) !== 0) eliminations.push({ cellIndex: i, digit: d });
      }
      if (eliminations.length === 0) continue;
      return makeBoxLineStep('box-to-line', box, line, d, inBox, eliminations);
    }
  }
  return null;
}

function sharedBox(cells: readonly number[]): number | null {
  if (cells.length < 2) return null;
  const first = boxOfIndex(cells[0] as number);
  for (let i = 1; i < cells.length; i++) {
    if (boxOfIndex(cells[i] as number) !== first) return null;
  }
  return first;
}

export function findBoxLineReduction(state: CandidateState): BoxLineStep | null {
  const kinds: Array<'row' | 'col'> = ['row', 'col'];
  for (const kind of kinds) {
    for (let idx = 0; idx < BOARD_SIZE; idx++) {
      const line: Unit = { kind, index: idx };
      for (let n = 1; n <= BOARD_SIZE; n++) {
        const d = n as NonEmptyDigit;
        const where = digitCellsInUnit(line, d, state);
        const box = sharedBox(where);
        if (box === null) continue;
        const dBit = bit(d);
        const eliminations: Elimination[] = [];
        for (const i of cellsOfUnit({ kind: 'box', index: box })) {
          if (where.includes(i)) continue;
          if (((state.cells[i] ?? 0) & dBit) !== 0) eliminations.push({ cellIndex: i, digit: d });
        }
        if (eliminations.length === 0) continue;
        return makeBoxLineStep('line-to-box', box, line, d, where, eliminations);
      }
    }
  }
  return null;
}
